import { useSchool } from "./schoolContext";

// ════════════════════════════════════════════════════════════════
// Plan Limits — La Tiendita CR
//
// Defines the feature limits for each plan and provides a hook
// to consume them in components. Enforcement is frontend-only for
// now (no Firestore rules changes needed for the initial rollout).
//
// Plans: "free" | "basic" | "premium"
// ════════════════════════════════════════════════════════════════

export const PLAN_NAMES = {
  free:    "Free",
  basic:   "Basic",
  premium: "Premium",
};

export const PLAN_COLORS = {
  free:    "#94A3B8",
  basic:   "#3B82F6",
  premium: "#F59E0B",
};

// ─── Plan feature definitions ───
// null means "unlimited"
export const PLAN_LIMITS = {
  free: {
    maxVendors:       2,
    maxStudents:      50,
    maxLocations:     1,
    // Feature flags
    pdfReports:       false,  // daily/weekly/monthly PDF export
    weeklyMenu:       false,  // menú semanal view in ProductsTab
    scheduledOrders:  false,  // student can schedule orders
    salesTab:         false,  // SalesTab visible
    emailNotifications: false,
    brandingRemoved:  false,  // "Powered by" watermark
    prioritySupport:  false,
    customBranding:   false,
  },
  basic: {
    maxVendors:       5,
    maxStudents:      300,
    maxLocations:     3,
    pdfReports:       true,
    weeklyMenu:       true,
    scheduledOrders:  true,
    salesTab:         true,
    emailNotifications: true,
    brandingRemoved:  true,
    prioritySupport:  false,
    customBranding:   false,
  },
  premium: {
    maxVendors:       null,   // unlimited
    maxStudents:      null,
    maxLocations:     null,
    pdfReports:       true,
    weeklyMenu:       true,
    scheduledOrders:  true,
    salesTab:         true,
    emailNotifications: true,
    brandingRemoved:  true,
    prioritySupport:  true,
    customBranding:   true,
  },
};

// ─── Upgrade messages ───
// Shown to admins when they try to use a locked feature
export const UPGRADE_MESSAGES = {
  pdfReports:       "Los reportes PDF están disponibles en el plan Basic o superior.",
  weeklyMenu:       "El menú semanal está disponible en el plan Basic o superior.",
  scheduledOrders:  "Los pedidos programados están disponibles en el plan Basic o superior.",
  salesTab:         "El tab de Ventas está disponible en el plan Basic o superior.",
  maxVendors:       (plan, max) => `Tu plan ${PLAN_NAMES[plan]} permite hasta ${max} vendedores. Actualizá a un plan superior para agregar más.`,
  maxLocations:     (plan, max) => `Tu plan ${PLAN_NAMES[plan]} permite hasta ${max} localidades. Actualizá a un plan superior para agregar más.`,
  maxStudents:      (plan, max) => `Tu plan ${PLAN_NAMES[plan]} permite hasta ${max} estudiantes. Actualizá a un plan superior.`,
};

// ─── usePlanLimits hook ───
// Returns the limits for the current school's plan, plus helper functions.
export function usePlanLimits() {
  const { school } = useSchool();
  const plan = school?.plan || "free";
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;

  // Returns true if the school is AT or OVER the limit for a given resource.
  // Pass the current count and the resource name.
  // Example: isAtLimit("maxVendors", vendors.length)
  function isAtLimit(resource, currentCount) {
    const max = limits[resource];
    if (max === null) return false; // unlimited
    return currentCount >= max;
  }

  // Returns an upgrade nudge message for a feature or resource limit.
  function upgradeMessage(feature, currentCount) {
    const msg = UPGRADE_MESSAGES[feature];
    if (!msg) return "Esta función no está disponible en tu plan actual.";
    if (typeof msg === "function") return msg(plan, limits[feature], currentCount);
    return msg;
  }

  return {
    plan,
    planName: PLAN_NAMES[plan] || "Free",
    planColor: PLAN_COLORS[plan] || PLAN_COLORS.free,
    limits,
    isAtLimit,
    upgradeMessage,
    // Convenience booleans
    canUsePdfReports:       limits.pdfReports,
    canUseWeeklyMenu:       limits.weeklyMenu,
    canUseScheduledOrders:  limits.scheduledOrders,
    canSeeSalesTab:         limits.salesTab,
    canUseEmailNotifications: limits.emailNotifications,
    hasBrandingRemoved:     limits.brandingRemoved,
    hasPrioritySupport:     limits.prioritySupport,
    hasCustomBranding:      limits.customBranding,
  };
}
